'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { isFeatureOn } from '@/licensing/features'

const AuthContext = createContext()

const CACHE_KEY = 'cs_session_user'
// El acceso se cachea aparte de la sesión: la app tiene que poder abrirse sin
// internet sin degradar al usuario a free. Ver licencias-patreon.md.
const ENTITLEMENT_CACHE_KEY = 'cs_entitlement'

const FREE = { tier: 'free', features: [] }

function readCached(key, fallback) {
    if (typeof window === 'undefined') return fallback
    try {
        const raw = window.localStorage.getItem(key)
        return raw ? JSON.parse(raw) : fallback
    } catch {
        return fallback
    }
}

function writeCached(key, value) {
    if (typeof window === 'undefined') return
    try {
        if (value) window.localStorage.setItem(key, JSON.stringify(value))
        else window.localStorage.removeItem(key)
    } catch {
        // localStorage puede fallar (modo privado, cuota) — no es crítico.
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [entitlement, setEntitlement] = useState(FREE)
    const [loading, setLoading] = useState(true)

    // Aplica un estado de acceso que ya vino del server (el de /api/auth/me o el
    // que devuelve el canje de una key) y lo deja cacheado para el próximo
    // arranque sin internet.
    const applyEntitlement = useCallback((next) => {
        const value = {
            tier: next?.tier ?? 'free',
            features: Array.isArray(next?.features) ? next.features : [],
        }
        setEntitlement(value)
        writeCached(ENTITLEMENT_CACHE_KEY, value)
        return value
    }, [])

    const refresh = useCallback(async () => {
        try {
            const res = await fetch('/api/auth/me', { credentials: 'same-origin' })
            if (!res.ok) throw new Error(`status ${res.status}`)
            const data = await res.json()
            setUser(data.user ?? null)
            writeCached(CACHE_KEY, data.user ?? null)
            applyEntitlement(data)
            return true
        } catch {
            // Server caído u offline: tolerancia offline — usamos lo último que
            // supimos. OJO: un fetch fallido NO es una baja de membresía; si
            // acá cayéramos a free, al usuario sin internet se le apagarían los
            // módulos que pagó.
            setUser(readCached(CACHE_KEY, null))
            setEntitlement(readCached(ENTITLEMENT_CACHE_KEY, FREE))
            return false
        }
    }, [applyEntitlement])

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            await refresh()
            if (!cancelled) setLoading(false)
        })()
        return () => {
            cancelled = true
        }
    }, [refresh])

    // El cliente NO reevalúa las reglas de quién ve qué: pregunta por la lista
    // que ya resolvió el server. `isFeatureOn` es el respaldo para el caso raro
    // de un caché viejo sin la lista.
    const hasFeature = useCallback(
        (key) => {
            if (entitlement.features?.length) return entitlement.features.includes(key)
            return isFeatureOn(entitlement.tier, key)
        },
        [entitlement],
    )

    const value = {
        user,
        loading,
        isAuthenticated: !!user,
        tier: entitlement.tier,
        features: entitlement.features,
        hasFeature,
        applyEntitlement,
        refresh,
        // Acceso pago real, resuelto en el server (antes era "cualquiera que
        // esté logueado", de cuando no había monetización).
        isPremiumEffective: entitlement.tier === 'pro',
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
